/**
	WDForm - Modern Unified Form System
		
		Form Container Events:
		form:change -> user changed a form value
		model:change -> underlying model was updated somehow (user or programmatic)
	*/

import WDElement from '../wd-element';
import WDButton from '../button/wd-button';
import { WDRest } from '../../core/fetch/index.js';
import { WDModel } from '../../core/wd-model.js';
import WDUtil from '../../core/wd-util.js';
import * as WDForms from './index-forms';

export default class WDForm extends WDElement
{
	#form;
	#model = null;
	#validationTimeout = null;
	#updatingFromModel = false;
	#updatingFromForm = false;

	#formConfig = {
		wrapper_class: 'col-12 col-sm-6',		//default to full width for mobile, half width above
		form_class: 'col-12',								//default to side-by-side with label on mobile, label over column aobve
		label_class: 'col-12',	 						//counterpart to form_class
		initFetch: true,
		model: null,
	};

	//
	constructor(opts) 
	{
		super();

		// Always create a model (can be empty)
		this.model = opts?.model || new WDModel({});

		// Apply configuration
		if (opts) Object.assign(this.#formConfig, opts);

		// Create form container
		this.#form = wdc('<form novalidate needs-validation class="wd-form"/>');
		this.append(this.#form);

		// Setup unified event handling
		this.#setupFormHandling();

		//passed a uri for the form configuration
		if (opts?.uri) this.addFormsFromURI(opts.uri);
		if (opts?.forms) this.addForms(opts.forms);

		return this;
	};

	// Setup model event binding for reactive updates
	#setupModelBinding() {

		//individual property in the model changes
		this.#model.on('change', (event) => this.#handleModelChange(event) );

		//whole model gets updated at once
		this.#model.on('batch', (event) => this.#handleModelBatch(event) );
	}

	// Setup form-level event handling
	#setupFormHandling() {
		this.on('submit', (evt) => { 
			evt.preventDefault(); 
			return false; 
		});
	}

	// Handle individual model changes
	#handleModelChange(event) {

		if (this.#updatingFromForm === true) return;

		this.#updatingFromModel = true;

		let { property, value, oldValue } = event;
		const formKey = this.#pathToFormKey(property);
		const formItem = this.getItem(formKey);

		//couldn't find the form for some reason
		if (!formItem) {
			this.#updatingFromModel = false;
			return;
		}

		if ( value === undefined ) value = null;		
		if (value != oldValue) formItem.value = value;

		this.#updatingFromModel = false;
	}

	// Handle batch model changes
	#handleModelBatch(event) {
		
		if (this.#updatingFromForm === true) return;

		this.#updatingFromModel = true;
		
		// Loop through all keys in the model and update form values
		Object.keys(this.#model).forEach(key => {

			const formKey = this.#pathToFormKey(key);
			const formItem = this.getItem(formKey);
			
			// If the form item exists, update its value from the model
			if (formItem) {
				const value = this.#model[key] !== undefined ? this.#model[key] : null;
				formItem.value = value;
			}
		});

		this.#updatingFromModel = false;
	}

	// Convert model path to form key notation
	#pathToFormKey(path) {
		if (typeof path === 'string' && path.includes('.')) {
			// Convert dot notation to bracket notation
			return path.replace(/\.([^.]+)/g, '[$1]');
		}
		return path;
	}

	// Convert form key to model path notation  
	#formKeyToPath(key) {
		if (typeof key === 'string' && key.includes('[')) {
			// Convert bracket notation to dot notation
			return key.replace(/\[([^\]]+)\]/g, '.$1');
		}
		return key;
	}

	// Setup individual form item events
	#setupFormItemEvents(formItem) {
		
		// Auto-sync with model no matter how the chnage comes about
		formItem.on('value:set', (evt) => {
			console.log("value set",formItem.name,formItem.value);
			this.#handleFormValueSet(formItem);
		});
	}

	// Handle form changes and sync to model
	#handleFormValueSet(formItem, evt) {
		
		if (this.#updatingFromModel === true) return;

		this.#updatingFromForm = true;

		// Update model efficiently
		const path = formItem.name;
		const value = formItem.value;
			
		if (path.includes('['))
		{
			// Handle nested object notation
			const objectData = this.toObjectNotation({ [path]: value });
			this.#model.merge(objectData);
		} 
		else
		{
			this.#model[path] = value;
		}

		this.#updatingFromForm = false;
	
	}


	// Apply form layout and styling
	#applyFormLayout(formItem, cfg) {
		if (cfg.label) this.addFormLabel(formItem, cfg);
		if (cfg.prefix && formItem.prefix) formItem.prefix.append(cfg.prefix);
		if (cfg.suffix && formItem.suffix) formItem.suffix.append(cfg.suffix);

		formItem.classList.add('wd-form-item', ...cfg.wrapper_class.split(' '), `wd-form-${cfg.name}`);
		formItem.dataset.formName = cfg.name;
	}

	// Enhanced form performance validation
	#performValidation() {
		let customValid = true;

		//loop through all forms.  Run any custom validation methods (for non-standard html elements)
		this.items.forEach(item => {
			if (typeof (item.validate) != 'undefined') {
				const v = item.validate();
				if (v === false) customValid = false;
			}
		});

		const valid = this.#form.checkValidity();
		this.classList.add('was-validated');

		return (valid === true && customValid === true);
	}

	// Getters and setters
	get model() { return this.#model; }
	set model(newModel) {

		if (newModel?.isProxy)
		{
			const replacingModel = this.#model !== null;

			//since we replace the entire model, treat like a batch change
			//and sync the form to it
			this.#model = newModel;
			this.#setupModelBinding();

			//we are replacing an existing model so trigger updates
			if (replacingModel) this.#handleModelBatch();

		}
		else if (this.#model)
		{
			//when does this actually occur?
			this.#model.replace(newModel);
		}
		else
		{
			this.#model = new WDModel(newModel);
		}
		
	}

	//allow adding of a single form config or an array of them
	addForms(items)
	{	
		items.forEach( item => {
			//allow appending of regular html elements
			if (item instanceof HTMLElement)
			{
				//may want to put this in a wrapper
				this.#form.append(item);
			}
			else
			{
				return this.addForm(item);
			}
		});
	};

	getFormDefs(uri)
	{
		return new Promise((resolve, reject) =>
		{
			const p = new WDRest(uri);
			p.req().then(formDefs =>
			{
				formDefs.forEach(def =>
				{
					//form has a uri for options, setup the fetch for it
					if (def?.uri)
					{
						def.fetch = new WDRest(def.uri);
						if (def?.uri_options) def.fetch.add(def.uri_options);
					}
				});

				resolve(formDefs);
			});

		});
	};

	//
	addFormsFromURI(uri)
	{
		return new Promise((resolve, reject) =>
		{
			this.getFormDefs(uri).then(formDefs =>
			{
				formDefs.forEach(def => this.addForm(def));
				resolve(true);

			})
		});
	};

	add(cfg) { return this.addForm(cfg); };

	//for later - double-appending gets dropdown fetch called twice
	addForm(cfg)
	{
		const f = this.buildForm(cfg);
		this.#form.append(f);
	
		return f;
	};

	buildForm(cfg)
	{
		//allow overrides from the config
		if (!cfg.wrapper_class) cfg.wrapper_class = this.#formConfig.wrapper_class;
		if (!cfg.form_class) cfg.form_class = this.#formConfig.form_class;
		if (!cfg.label_class) cfg.label_class = this.#formConfig.label_class;
		if (!cfg.type) cfg.type = 'text';

		// Generate unique ID if not provided
		const id = cfg.id || `form-${WDUtil.uuid()}`;

		// Enhanced config with model and form references
		const enhancedConfig = {
			...cfg,
			id,
			model: this.#model, // Pass model reference
			form: this // Pass form container reference
		};

		return this.buildFormObject(enhancedConfig);
	}

	/**
		*/
	addFormLabel(f, cfg)
	{
		f.classList.add('align-items-center');
		f.inputGroup.classList.add(cfg.form_class);

		let lbl = wdc('<label/>');
		lbl.classList.add('form-label', cfg.label_class);
		lbl.append(cfg.label);
		f.prepend(lbl);

		if (cfg.label_info)
		{
			let infoBtn = new WDButton({fasIcon:'circle-info',size:'sm'});
			infoBtn.on('click', () => new WDActivity().showMessage(cfg.label_info));
			lbl.append(infoBtn);
		}
	};

	/**
		Build form object using WDForms barrel import
		*/
	buildFormObject(cfg)
	{
		const formType = cfg.type.toLowerCase().replace('-', '');
		const fn = `WDForm${formType.capitalize()}`;

		// Synchronous class lookup from barrel import
		const FormClass = WDForms[fn];

		if (FormClass)
		{
			const f = new FormClass(cfg);
			
			// Apply layout and styling (sets data-form-name attribute)
			this.#applyFormLayout(f, cfg);

			// Setup enhanced event handling
			this.#setupFormItemEvents(f);

			// Sync with model if value exists
			const value = cfg.name.includes('[') ? this.getValueFromBracketNotation(cfg.name) : this.#model[cfg.name];
			if (value !== undefined) f.value = value;
		
			return f;
		}
		else
		{
			console.error(`Form class ${fn} not found in WDForms`, err);
			reject(false);
		}
		
	};

	getItem(key)
	{
		// DOM-based lookup using data-form-name attribute
		return this.querySelector(`[data-form-name="${key}"]`);
	}

	getFormById(id)
	{
		// DOM-based lookup using id
		return this.querySelector(`#${id}`);
	}

	// Efficient bulk operations
	setBulkData(data) {
		this.#model.batch(() => {
			Object.entries(data).forEach(([key, value]) => {
				this.#model[key] = value;
			});
		});
	}

	// Suspended updates during initialization
	initialize(data) {
		this.#model.withSuspended(() => {
			this.setBulkData(data);
		});
	}

	// Utility method for bracket notation values
	getValueFromBracketNotation(path) {
		const dotPath = path.replaceAll('[', '.').replaceAll(']', '');
		return this.getValueFromDotNotation(dotPath);
	}

	getValueFromDotNotation(path) {
		// Navigate through the model using dot notation
		let current = this.#model;
		const parts = path.split('.');
		
		for (const part of parts) {
			if (current && typeof current === 'object' && part in current) {
				current = current[part];
			} else {
				return undefined;
			}
		}
		
		return current;
	}

	toFormNotation(obj, prefix, result)
	{
		result = result || {};

		// iterate over all properties
		for (let prop in obj) 
		{
			if (obj.hasOwnProperty(prop)) 
			{
				let value = obj[prop];

				// build the property name for the result object
				// first level is without square brackets
				let name = prefix ? prefix + '[' + prop + ']' : prop;

				let fm = this.getItem(name);
				let stopDescent = false;

				if (fm && typeof (fm) != 'function')
				{
					//these form types expect objects, so don't descent any further down
					let objForms = ['button', 'picker', 'textpicker'];
					stopDescent = objForms.indexOf(fm.type != -1);
				}

				if (typeof value !== 'object' || stopDescent) 
				{
					// not an object, add value to final result
					result[name] = value;
				}
				else 
				{
					// object, go deeper
					this.toFormNotation(value, name, result);
				}
			}
		}

		return result;
	}

	toObjectNotation(formData)
	{
		Object.keys(formData).forEach(k =>
		{

			const path = k.replace(/\[/g, '.').replace(/\]/g, '').split('.');
			const last = path.pop();
			const isNumber = parseInt(last) == last;		//"last" is the key.  If it's a number, we make an array instead of object

			if (path.length) 
			{
				path.reduce((o, p) => o[p] = o[p] || (isNumber ? [] : {}), formData)[last] = formData[k];
				delete formData[k];
			}
		});

		return formData;
	};

	/**
		Enhanced data getter/setter using WDModel.
		If passed values, sets the data object and updates all the forms
		*/
	get value()
	{
		// Use WDModel's efficient serialization
		return this.#model.toObject();
	}

	set value(values)
	{
		if (!values) return;

		// Use batch operation for better performance
		this.#model.batch(() => {
			//convert values to form notation
			const formData = this.toFormNotation(values);

			//populate the fields
			Object.keys(formData).forEach(key => {
				const value = formData[key];

				//this would happen if working w/ a WDProto object
				if (typeof (value) != 'function') {
					const formItem = this.getItem(key);
					if (formItem) {
						formItem.value = value;
					}
					// Also update the model directly
					this.#model[this.#formKeyToPath(key)] = value;
				}
			});
		});
	}
	
	set readonly(ro)
	{
		this.items.forEach( item => item.readonly = ro );
	}

	validate()
	{
		// Debounced validation for better performance
		if (this.#validationTimeout) {
			clearTimeout(this.#validationTimeout);
		}
		
		this.#validationTimeout = setTimeout(() => {
			return this.#performValidation();
		}, 100);
		
		// For immediate return, perform validation synchronously
		return this.#performValidation();
	}

	reset()
	{
		// Use batch operation for performance
		this.#model.batch({});
		
		this.#form.replaceChildren();
	}

	get items()
	{
		const output = [];

		//only return forms that are our custom form elements		
		this.querySelectorAll('.wd-form-item').forEach(item =>
		{
			if (item?.type) output.push(item);
		});

		return output;
	}

	//standard <action>Model naming scheme
	fetchModel() { return this.req(); };
	saveModel() { return this.post(); };
	deleteModel() { return this.del(); };

	// Enhanced model integration methods
	req() { 
		return this.#model.req();
	}
	
	post() { 
		return this.#model.post();
	}
	
	del() { 
		return this.#model.del();
	}

	// Action management for conditional form behavior
	addAction(action, srcForm, watchForm, watchVal) {
		const actionHandler = () => {
			const sf = this.getItem(srcForm);
			const wf = this.getItem(watchForm);

			if (!sf) console.error('Could not find form for action', srcForm);

			if (watchVal == wf.value) {
				if (action == 'hide') sf.hide();
				else if (action == 'show') sf.show();
				else if (action == 'set_required') sf.required = true;
				else if (action == 'unset_required') sf.required = false;
				else if (action == 'set_readonly') sf.readonly = true;
				else if (action == 'unset_readonly') sf.readonly = false;
			}
		};

		this.#model.on('change', watchForm, actionHandler);

		//if the value is set in the model already, trigger the handler
		if (typeof this.#model[watchForm] !== 'undefined') setTimeout( actionHandler, 10);
	}

	addToggleAction(srcForm, watchForm, showVal, hideVal) {
		this.addAction('hide', srcForm, watchForm, hideVal);
		this.addAction('show', srcForm, watchForm, showVal);
	}

	addRequiredAction(srcForm, watchForm, reqVal, unReqVal) {
		this.addAction('set_required', srcForm, watchForm, reqVal);
		this.addAction('unset_required', srcForm, watchForm, unReqVal);
	}

	addReadonlyAction(srcForm, watchForm, reqVal, unReqVal) {
		this.addAction('set_readonly', srcForm, watchForm, reqVal);
		this.addAction('unset_readonly', srcForm, watchForm, unReqVal);
	}

	/**
	 * Cleanup callback - called when form is truly removed from DOM
	 * Inherits from WDElement's deferred cleanup pattern
	 */
	cleanupCallback()
	{
		// Clear validation timeout
		if (this.#validationTimeout) {
			clearTimeout(this.#validationTimeout);
			this.#validationTimeout = null;
		}
		
		// Clear model listeners
		if (this.#model) {
			this.#model = null;
		}
		
		// Clear form references
		this.#form = null;
		this.#formConfig = null;
		
		// Call parent cleanup
		super.cleanupCallback();
	}

};

customElements.define('wd-form', WDForm);
